// Materialization core shared by the Builder and the Custom Dashboards feature.
//
// The Builder's data effect chooses between two EQL routes (server-side
// `aggregate` for a single aggregatable axis, client-side `raw` cross for
// everything else — see Builder.jsx / README). That route logic lived inline in
// the component; it is lifted here so a Custom Dashboard can re-run the very
// same query to *freeze* (and later *refresh*) a piece's result, with no React.
//
//   fetchPivotData(cfg, deps)  -> { mode, pivot|records, meta }
//       The raw path returns `records` (not a pivot) so the Builder can keep
//       applying its client-side Filters zone via a memo without refetching.
//
//   materializeView(state, deps) -> { pivot, meta }
//       Higher-level: runs fetchPivotData and, for the raw path, applies
//       computePivot with the (already-frozen) filterValues. This is what the
//       dashboard uses — after a snapshot there is no interactive Filters zone.

import { eqlAggregate, eqlAssignedReportIds, eqlQueryAll } from "../../../../apis/apiEQL";
import { computePivot, pivotFromAggregate } from "./pivot";
import { flattenRows, isTruthyFlag } from "./flattenRows";
import { assignedUserOf, restrictToReportIds, scopeToClosed } from "./queryFilters";
import { DEFAULT_STAT, DURATION_FIELDS, isDurationMeasure } from "./measures";

// Safety cap for the client-side (raw) path. Exported so the Builder's
// "truncated" notice stays in sync with the actual limit.
export const MAX_RECORDS = 5000;

/** Display label for an aggregate group value, per dimension. */
export const aggregateLabelOf = (dim, lbl) => (value) => {
  if (dim.isCatalog) return lbl.catalogLabel(dim, value);
  if (dim.isIndicator) return isTruthyFlag(value) ? lbl.yes : lbl.no;
  switch (dim.key) {
    case "status":
      return lbl.statusLabel(value);
    case "entryway":
      return lbl.entrywayLabel(value);
    case "severity":
      return lbl.severityLabel(value);
    case "closed_type":
      return lbl.closedTypeLabel(value);
    default:
      return String(value);
  }
};

/**
 * Fetches the data for a pivot configuration, choosing the EQL route the same
 * way Builder.jsx does. Pure of React.
 *
 * @param {Object} cfg
 * @param {{rows:string[],cols:string[],filters:string[]}} cfg.assignments
 * @param {Object|undefined} cfg.appliedFilters  EQL filter tree
 * @param {Object<string,number>} [cfg.depthByCatalog]  per-catalog hierarchy depth
 * @param {string} [cfg.measure]  a duration measure key (see measures.js);
 *   forces the raw route, since EQL `aggregate` only counts
 * @param {Object} deps
 * @param {Object<string,Object>} deps.dimByKey  dimension lookup by key
 * @param {Object} deps.labels  label helpers (statusLabel, catalogLabel, ...)
 * @returns {Promise<{mode:'aggregate'|'raw', pivot?:Object, records?:Array, meta:Object}>}
 */
export const fetchPivotData = async (cfg, deps) => {
  const { assignments, appliedFilters, depthByCatalog = {}, measure } = cfg;
  const withDurations = isDurationMeasure(measure);
  const { dimByKey, labels } = deps;

  const rows = assignments?.rows || [];
  const cols = assignments?.cols || [];
  const filterZone = assignments?.filters || [];

  const catalogDepth = (key) => depthByCatalog[key] ?? 1;

  const rowDim = rows.length === 1 ? dimByKey[rows[0]] : null;
  const canAggregate =
    !withDurations &&
    rows.length === 1 &&
    cols.length === 0 &&
    filterZone.length === 0 &&
    !!rowDim?.aggregatable;

  const usedDimKeys = [...new Set([...rows, ...cols, ...filterZone])];

  // Closure type only exists on closed reports; scope the query so open ones
  // don't leak into the "no type" ("—") bucket. See scopeToClosed.
  const needsClosedScope = usedDimKeys.includes("closed_type");

  // Assigned user: the query stays exactly as configured, minus the reports the
  // chosen user does not have in Assignment.assigned_reports. Looked up on every
  // run (not frozen at Apply) so saved views and refreshed pieces see current
  // assignments. See restrictToReportIds.
  const assignedUserId = assignedUserOf(appliedFilters);
  const userScoped =
    assignedUserId == null
      ? appliedFilters
      : restrictToReportIds(appliedFilters, await eqlAssignedReportIds(assignedUserId));

  const effectiveFilters = scopeToClosed(userScoped, needsClosedScope);

  if (canAggregate) {
    const resp = await eqlAggregate({
      groupBy: rowDim.groupBy,
      depth: rowDim.isCatalog ? catalogDepth(rowDim.key) : undefined,
      filters: effectiveFilters,
    });
    const pivot = pivotFromAggregate(
      resp?.data,
      rowDim.key,
      aggregateLabelOf(rowDim, labels),
      // closed_type: label the null group "en proceso de cierre".
      rowDim.key === "closed_type" ? labels.closedTypeLabel(null) : undefined
    );
    return {
      mode: "aggregate",
      pivot,
      meta: {
        mode: "aggregate",
        total: resp?.metadata?.total_records ?? 0,
        truncated: false,
      },
    };
  }

  const used = usedDimKeys
    .map((k) => dimByKey[k])
    .filter(Boolean)
    // Attach each catalog's own depth for the client-side flatten.
    .map((d) => (d.isCatalog ? { ...d, depth: catalogDepth(d.key) } : d));
  const eqlFields = [
    ...new Set([...used.map((d) => d.field), ...(withDurations ? DURATION_FIELDS : [])]),
  ];

  const { rows: rawRows, total, truncated } = await eqlQueryAll({
    fields: eqlFields,
    filters: effectiveFilters,
    max: MAX_RECORDS,
  });

  const records = flattenRows(rawRows, used, { labels, withDurations });
  return { mode: "raw", records, meta: { mode: "raw", total, truncated } };
};

/**
 * The measure computePivot expects for a view state: null for Count, else
 * { key, stat }.
 */
export const pivotMeasureOf = ({ measure, stat } = {}) =>
  isDurationMeasure(measure) ? { key: measure, stat: stat || DEFAULT_STAT } : null;

/**
 * Runs a full Builder view to a final pivot. Used by Custom Dashboards to freeze
 * (and later refresh) a piece — at snapshot time the Filters zone is fixed, so
 * `filterValues` is applied here rather than left to a live memo.
 *
 * @param {Object} state  deserialized Builder view state
 *   ({ assignments, appliedFilters, filterValues (Sets), depthByCatalog,
 *   measure, stat })
 * @param {Object} deps   { dimByKey, labels }
 * @returns {Promise<{pivot: Object|null, meta: Object|null}>}
 */
export const materializeView = async (state, deps) => {
  const rows = state?.assignments?.rows || [];
  const cols = state?.assignments?.cols || [];
  if (!rows.length && !cols.length) return { pivot: null, meta: null };

  const res = await fetchPivotData(state, deps);
  if (res.mode === "aggregate") return { pivot: res.pivot, meta: res.meta };

  const pivot = computePivot(
    res.records,
    rows,
    cols,
    state.filterValues || {},
    pivotMeasureOf(state)
  );
  return { pivot, meta: res.meta };
};
